import { translate } from '../../common/language/translations';
import { FormationConfig } from '../types/formation';
import { DataPosition, PlatformPosition, PositionData, PositionGroup } from '../types/position';


// get the display string for a given position list
export const getDisplayPositions = (
  positionList: string[] | undefined,
  language: string | undefined,
  noWhitespace?: boolean,
): string | undefined => {
  if (positionList && positionList.length > 0) {
    const positions = positionList.map(position => {
      return translate(position, language);
    });

    return positions.join(noWhitespace ? '/' : ' / ');
  }
};


// get the display string for a given position list, used in text as part of a sentence
export const getTextDisplayPositions = (
  positionList: string[] | undefined,
  language: string | undefined,
): string | undefined => {
  if (positionList && positionList.length > 0) {
    if (positionList.length === 1) {
      return translate(positionList[0], language);
    }
    let displayString = '';
    positionList.forEach((position, index) => {
      displayString += translate(position, language);
      if (index < positionList.length - 2) {
        displayString += ', ';
      }
      else if (index < positionList.length - 1) {
        displayString += ' ' + translate('and', language, true) + ' ';
      }
    });
    return displayString;
  }
};


// get the grid position (in percentages) for the given x and y coordinates
export const getGridPosition = (
  x: number,
  y: number,
  scalePosition?: boolean, // if a formation has goalkeeper but the team does not, we want to scale the outfield positions to cover more of the field
): { left: number; top: number } => {

  const rowWidth = 69.2 + (y * 0.74);
  const rowOffset = (100 - rowWidth) / 2;
  const leftPercentage = rowOffset + (x / 14) * rowWidth;

  const columnHeight = 78.1;
  const columnOffset = (100 - columnHeight) / 2;

  const numberOfRows = scalePosition ? 15 : 16;
  const topPercentage = columnOffset + (y / numberOfRows) * columnHeight;

  return {
    left: leftPercentage,
    top: topPercentage
  };
};


// get list of primary and secondary positions based on a list of positions with minutes played from multiple iterations
const dominantThreshold = 0.4;
const primaryThreshold = 0.25;
const relativePrimaryThreshold = 0.8;
const secondaryThreshold = 0.1;
export const getPrimaryAndSecondaryPositionsForMultipleIterations = (
  positions: PositionData[],
): { primaryPositions: string[]; secondaryPositions: string[] } => {

  const merged: { [position: string]: number } = {};
  positions.forEach(({ position, minutes }) => {
    merged[position] = (merged[position] || 0) + minutes;
  });

  const totalMinutes = Object.values(merged).reduce((sum, m) => sum + m, 0);
  if (totalMinutes === 0) {
    return { primaryPositions: [], secondaryPositions: [] };
  }

  const normalizedScores: { position: string; percentage: number; }[] = Object.entries(merged).map(([position, minutes]) => ({
    position,
    percentage: minutes / totalMinutes,
  }));

  normalizedScores.sort((a, b) => b.percentage - a.percentage);

  const primaryPositions: string[] = [];
  const secondaryPositions: string[] = [];

  const highestScore = normalizedScores[0]?.percentage ?? 0;

  normalizedScores.forEach(({ position, percentage }) => {
    if (percentage >= dominantThreshold || (percentage >= primaryThreshold && percentage >= relativePrimaryThreshold * highestScore)) {
      primaryPositions.push(position);
    }
    else if (percentage >= secondaryThreshold) {
      secondaryPositions.push(position);
    }
  });

  return { primaryPositions, secondaryPositions };
};


// get all positions that are considered comparable to a given position group, for the given formation
export const getPositionsToCompareAgainst = (
  positionGroup: PositionGroup,
  formation: FormationConfig,
  primaryPosition?: DataPosition, // used for tie breaking in some cases
): PlatformPosition[] => {

  let compareablePositions: PlatformPosition[] = [];

  // GK
  if (positionGroup === 'GK') {
    // only compared against GK
    compareablePositions = ['GK'];
    return compareablePositions.filter(position => formation[position]);
  }

  // S
  if (positionGroup === 'S') {

    // tier 1 comparison
    compareablePositions = ['LS', 'S', 'RS'];
    compareablePositions = compareablePositions.filter(position => formation[position]);
    if (compareablePositions.length > 0) {
      return compareablePositions;
    }

    // tier 2 comparison
    compareablePositions = ['LW', 'RW', 'LCAM', 'RCAM', 'CAM'];
    return compareablePositions.filter(position => formation[position]);
  }

  // CB
  if (positionGroup === 'CB') {
    // tier 1 comparison
    compareablePositions = ['LCB', 'CB', 'RCB'];
    compareablePositions = compareablePositions.filter(position => formation[position]);
    if (compareablePositions.length > 0) {
      return compareablePositions;
    }

    // tier 2 comparison
    compareablePositions = ['LB', 'RB', 'LCDM', 'CDM', 'RCDM'];
    return compareablePositions.filter(position => formation[position]);
  }

  // CM
  if (positionGroup === 'CM') {
    // tier 1 comparison
    compareablePositions = ['LCM', 'CM', 'RCM', 'LCDM', 'CDM', 'RCDM'];
    compareablePositions = compareablePositions.filter(position => formation[position]);
    if (compareablePositions.length > 0) {
      return compareablePositions;
    }

    // tier 2 comparison
    compareablePositions = ['LCAM', 'CAM', 'RCAM', 'LM', 'RM'];
    return compareablePositions.filter(position => formation[position]);
  }

  // FB
  if (positionGroup === 'FB') {
    // tier 1 comparison
    compareablePositions = ['LB', 'RB', 'LWB', 'RWB'];
    compareablePositions = compareablePositions.filter(position => formation[position]);
    if (compareablePositions.length > 0) {
      return compareablePositions;
    }

    // tier 2 comparison
    compareablePositions = ['LM', 'RM'];
    compareablePositions = compareablePositions.filter(position => formation[position]);
    if (compareablePositions.length > 0) {
      return compareablePositions;
    }

    // tier 3 comparison
    compareablePositions = ['LW', 'RW'];
    compareablePositions = compareablePositions.filter(position => formation[position]);
    if (compareablePositions.length > 0) {
      return compareablePositions;
    }

    // tier 4 comparison
    compareablePositions = ['LCB', 'CB', 'RCB'];
    return compareablePositions.filter(position => formation[position]);
  }

  if (positionGroup === 'W') {
    // tier 1 comparison
    compareablePositions = ['LW', 'RW', 'LCAM', 'RCAM', 'CAM'];
    compareablePositions = compareablePositions.filter(position => formation[position]);
    if (compareablePositions.length > 0) {
      return compareablePositions;
    }

    if (primaryPosition === 'CAM') {
      compareablePositions = ['LCM', 'CM', 'RCM'];
      compareablePositions = compareablePositions.filter(position => formation[position]);
      if (compareablePositions.length > 0) {
        return compareablePositions;
      }
    }

    // tier 2 comparison
    compareablePositions = ['LM', 'RM'];
    compareablePositions = compareablePositions.filter(position => formation[position]);
    if (compareablePositions.length > 0) {
      return compareablePositions;
    }

    // tier 3 comparison
    compareablePositions = ['LS', 'S', 'RS'];
    compareablePositions = compareablePositions.filter(position => formation[position]);
    if (compareablePositions.length > 0) {
      return compareablePositions;
    }

    // tier 4 comparison
    compareablePositions = ['LCM', 'CM', 'RCM'];
    return compareablePositions.filter(position => formation[position]);
  }

  return [];
};
